/**
 * Mock note template options for the appointment page when adding a note
 * and no templates are returned from the API (e.g. offline or empty Template Library).
 */
import type { Questionnaire } from '@medplum/fhirtypes';
import type { NoteTemplateOption } from './hooks';
import {
  LATE_CANCEL_SESSION_NOTE_QUESTIONNAIRE,
  NO_SHOW_SESSION_NOTE_QUESTIONNAIRE,
  PROGRESS_NOTE_QUESTIONNAIRE,
} from './noteTemplates';
import { getMockTemplates } from '../settings/template-library/mockTemplates';

/** Appointments tab category in Template Library. */
const APPOINTMENTS_CATEGORY = 'appointments';

/** Service-specific questionnaires (structure only; no pre-filled content). */
function familyNoteQuestionnaire(): Questionnaire {
  return {
    resourceType: 'Questionnaire',
    id: 'tpl-family-note',
    /** Must match pilot seed `buildNoteTemplates` (`name` + canonical `url`) so resolve finds the server Questionnaire. */
    name: 'family-therapy-progress-note',
    url: 'https://unify.health/fhir/Questionnaire/family-therapy-progress-note',
    title: 'Family Therapy Progress Note',
    status: 'active',
    item: [
      { linkId: 'title', text: 'Title', type: 'string' },
      { linkId: 'membersPresent', text: 'Family members present', type: 'text' },
      { linkId: 'presentingConcerns', text: 'Presenting concerns today', type: 'text' },
      { linkId: 'dynamicsObserved', text: 'Family dynamics and interaction patterns', type: 'text' },
      { linkId: 'progressGoals', text: 'Progress toward treatment goals', type: 'text' },
      { linkId: 'plan', text: 'Plan / Next steps', type: 'text' },
    ],
  };
}

function couplesNoteQuestionnaire(): Questionnaire {
  return {
    resourceType: 'Questionnaire',
    id: 'tpl-couples-note',
    name: 'couples-therapy-progress-note',
    url: 'https://unify.health/fhir/Questionnaire/couples-therapy-progress-note',
    title: 'Couples Therapy Progress Note',
    status: 'active',
    item: [
      { linkId: 'title', text: 'Title', type: 'string' },
      { linkId: 'attendance', text: 'Attendance', type: 'string' },
      { linkId: 'topicsDiscussed', text: 'Primary topic(s) discussed', type: 'text' },
      { linkId: 'interactionPattern', text: 'Couple interaction and communication', type: 'text' },
      { linkId: 'relationshipProgress', text: 'Progress on relationship goals', type: 'text' },
      { linkId: 'plan', text: 'Plan / Next steps', type: 'text' },
    ],
  };
}

function iopGroupNoteQuestionnaire(): Questionnaire {
  return {
    resourceType: 'Questionnaire',
    id: 'tpl-iop-group-note',
    name: 'iop-group-session-note',
    url: 'https://unify.health/fhir/Questionnaire/iop-group-session-note',
    title: 'IOP Group Session Note',
    status: 'active',
    item: [
      { linkId: 'title', text: 'Title', type: 'string' },
      { linkId: 'moduleTopic', text: 'Group module / topic', type: 'string' },
      { linkId: 'clientReport', text: 'Client self-report and symptom check-in', type: 'text' },
      { linkId: 'participation', text: 'Participation and skills practiced', type: 'text' },
      { linkId: 'riskLevel', text: 'Risk level and step-down readiness', type: 'text' },
      { linkId: 'plan', text: 'Plan / Coordination', type: 'text' },
    ],
  };
}

function groupNoteQuestionnaire(): Questionnaire {
  return {
    resourceType: 'Questionnaire',
    id: 'tpl-group-note',
    name: 'group-session-note',
    url: 'https://unify.health/fhir/Questionnaire/group-session-note',
    title: 'Group Therapy Progress Note',
    status: 'active',
    item: [
      { linkId: 'title', text: 'Title', type: 'string' },
      { linkId: 'groupTopic', text: 'Group topic / theme', type: 'string' },
      { linkId: 'processObservations', text: 'Group process and observations', type: 'text' },
      { linkId: 'participationNotes', text: 'Individual participation notes', type: 'text' },
      { linkId: 'plan', text: 'Plan / Next steps', type: 'text' },
    ],
  };
}

const OUTCOME_NOTE_OPTIONS: NoteTemplateOption[] = [
  {
    id: LATE_CANCEL_SESSION_NOTE_QUESTIONNAIRE.id!,
    title: LATE_CANCEL_SESSION_NOTE_QUESTIONNAIRE.title ?? 'Late Cancel Note',
    questionnaire: LATE_CANCEL_SESSION_NOTE_QUESTIONNAIRE,
  },
  {
    id: NO_SHOW_SESSION_NOTE_QUESTIONNAIRE.id!,
    title: NO_SHOW_SESSION_NOTE_QUESTIONNAIRE.title ?? 'No Show Note',
    questionnaire: NO_SHOW_SESSION_NOTE_QUESTIONNAIRE,
  },
];

const SPECIALISED_OPTIONS: NoteTemplateOption[] = [
  { id: 'tpl-family-note', title: 'Family Therapy Progress Note', questionnaire: familyNoteQuestionnaire() },
  { id: 'tpl-couples-note', title: 'Couples Therapy Progress Note', questionnaire: couplesNoteQuestionnaire() },
  { id: 'tpl-iop-group-note', title: 'IOP Group Session Note', questionnaire: iopGroupNoteQuestionnaire() },
  { id: 'tpl-group-note', title: 'Group Therapy Progress Note', questionnaire: groupNoteQuestionnaire() },
];

/** Mock appointment note templates (embedded Questionnaire definitions). */
export function getMockAppointmentNoteTemplates(): NoteTemplateOption[] {
  const comprehensive: NoteTemplateOption = {
    id: PROGRESS_NOTE_QUESTIONNAIRE.id!,
    title: PROGRESS_NOTE_QUESTIONNAIRE.title ?? 'Progress Note',
    questionnaire: PROGRESS_NOTE_QUESTIONNAIRE,
  };
  const fromLibrary = getMockTemplates()
    .filter((t) => t.category === APPOINTMENTS_CATEGORY)
    .map((t) => ({
      id: t.id,
      title: t.title,
      questionnaire: t.resource,
    }));
  const seen = new Set([comprehensive.id]);
  const rest = fromLibrary.filter((t) => {
    if (seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });

  return [comprehensive, ...OUTCOME_NOTE_OPTIONS, ...rest, ...SPECIALISED_OPTIONS];
}
